import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  PhoneIcon, VideoIcon, XIcon, MicIcon, MicOffIcon,
  VideoOffIcon, SettingsIcon, VolumeIcon, UserIcon, ScreenShareIcon, PlusIcon,
  UsersIcon, PlayIcon, MaximizeIcon, MinimizeIcon, PictureInPictureIcon
} from '../icons'
import { useAuth } from '../context/AuthContext'
import { avatarUrlById } from '../utils/avatar'
import { usePiP } from '../hooks/usePiP'
import type { GroupChannelMember , PublicUser } from '../types'
import type { CallState } from '../context/CallContext'
import { API_URL } from '../api'


// Module-level cache so CallView remounts (navigating between pages)
// don't trigger a refetch of /api/users every time.
let _userNamesCache: Record<string, string> = {}
let _userAvatarsCache: Record<string, string> = {}

export function CallView({ call, onClose, embedded = false, hidden = false }: { call: CallState; onClose: () => void; embedded?: boolean; hidden?: boolean }) {
  const {
    callState, isVideo, remoteStreams, participants, error, micError,
    muted, videoOff, devices, selectedDevices,
    inputVolume, outputVolume, screenSharing, reconnecting,
    localVideoRef, localStream, endCall, endCallForEveryone, toggleMute, muteRemote, unmuteRemote, toggleVideo,
    toggleScreenShare,
    changeDevice, changeInputVolume, changeOutputVolume,
    playSoundToAll,
    isChannelCall, channelId, inviteToCall, inviteToCallSilent, forceJoinCall, removeFromCall, loadDevices,
    remoteMuteStates, remoteSpeaking, invitingUsers,
  } = call

  const { authFetch } = useAuth()
  const [showSettings, setShowSettings] = useState(false)
  const [showInvite, setShowInvite] = useState(false)
  const [showSounds, setShowSounds] = useState(false)
  const [channelMembers, setChannelMembers] = useState<GroupChannelMember[]>([])
  const [userNames, setUserNames] = useState<Record<string, string>>({})
  const [userAvatars, setUserAvatars] = useState<Record<string, string>>({})
  const remoteAudioRefs = useRef<Record<string, HTMLVideoElement>>({})
  // Persistent <audio> elements for remote audio playback. Unlike the
  // <video> elements (which unmount/remount when switching between grid
  // and focus mode), these <audio> elements are ALWAYS in the DOM during
  // the call. This prevents "media was removed from the document" errors
  // when play() is interrupted by a UI layout change.
  const remoteAudioOnlyRefs = useRef<Record<string, HTMLAudioElement>>({})
  // Dedicated ref for the focus-mode main video element. This is NOT
  // shared with remoteAudioRefs (thumbnails) — sharing caused race
  // conditions where the stream was not re-attached after onunmute
  // replaced the MediaStream, making the remote video go black.
  const focusVideoRef = useRef<HTMLVideoElement | null>(null)
  // Focus mode: which tile is shown as the main (large) view.
  // null = grid layout (all tiles equal size)
  // 'local' = local video/camera is the main view
  // '<userId>' = that remote participant is the main view
  const [focusedTile, setFocusedTile] = useState<string | null>(null)
  // Ref to the focus-mode main tile — target of the Fullscreen API so the
  // shared screen (or any focused participant) can fill the whole display.
  const focusMainRef = useRef<HTMLDivElement | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Refs for the video area and controls bar — rendered into the PiP
  // window via React portal when pipActive is true.
  const videoAreaRef = useRef<HTMLDivElement | null>(null)
  const controlsRef = useRef<HTMLDivElement | null>(null)

  const { pipActive, pipContainer, openPiP, closePiP, pipSupported } = usePiP({
    localVideoRef,
    isActive: callState !== 'idle' && callState !== 'failed',
  })

  // Track tab visibility. Only visibilitychange — it fires when the
  // tab is actually hidden (switched tabs, minimized). We do NOT use
  // window blur/focus because blur fires when the window loses focus
  // even if the tab is still visible (e.g. clicking another app on
  // the same screen, or another monitor). That would open PiP when
  // the call is still visible on screen.
  const [tabVisible, setTabVisible] = useState(document.visibilityState !== 'hidden')
  useEffect(() => {
    const onVis = () => setTabVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  // Whether to show call content in the PiP portal (vs in the page).
  // Content goes to PiP only when the tab is hidden or the user
  // navigated away from the call page. When on the call page with the
  // tab visible, content stays in the embedded view — PiP stays open
  // with a placeholder so it's ready when the user leaves.
  const showInPiP = pipActive && pipContainer && (hidden || !tabVisible)

  // Derived: list of remote participant IDs. Defined early so useEffects
  // below can reference it in their dependency arrays without hitting the
  // temporal dead zone (TDZ) of a `const` declared later.
  const remoteUserIds = Object.keys(remoteStreams)
  // allKnownParticipants includes users who joined but haven't published
  // tracks yet (e.g. getUserMedia still loading or failed). Used for the
  // "aguardando participantes" check and participant count.
  const allKnownParticipants = participants.length > 0 ? participants : remoteUserIds
  const totalParticipants = allKnownParticipants.length + 1

  // Re-attach local stream when the <video> element remounts.
  // The local <video> is rendered in different positions depending on
  // focusedTile (focus mode vs grid mode vs thumbnail), so when the focus
  // changes, the element unmounts and a new one mounts. This effect re-
  // attaches srcObject whenever that happens.
  useEffect(() => {
    if (localStream && localVideoRef.current) {
      if (localVideoRef.current.srcObject !== localStream) {
        localVideoRef.current.srcObject = localStream
        localVideoRef.current.play().catch(() => {})
      }
    }
  }, [showInPiP, localStream, focusedTile, remoteUserIds])

  // Fetch user names and avatars for display (cached at module level
  // so CallView remounts don't trigger a refetch every time).
  useEffect(() => {
    if (Object.keys(_userNamesCache).length > 0) {
      setUserNames(_userNamesCache)
      setUserAvatars(_userAvatarsCache)
      return
    }
    authFetch(`${API_URL}/api/users`)
      .then((r) => r.json())
      .then((data) => {
        const names: Record<string, string> = {}
        const avatars: Record<string, string> = {}
        ;(data.users || []).forEach((u: PublicUser) => {
          names[u.id] = u.display_name || u.name || u.email || 'Usuário'
          if (u.avatar_url) avatars[u.id] = u.avatar_url
        })
        _userNamesCache = names
        _userAvatarsCache = avatars
        setUserNames(names)
        setUserAvatars(avatars)
      })
      .catch(() => {})
  }, [authFetch])

  const getUserName = (uid: string) => userNames[uid] || 'Usuário'
  const getUserAvatar = (uid: string) => userAvatars[uid]

  // Render a user's profile photo (avatar) or fall back to the generic
  // UserIcon. Used for tiles where the camera is off — instead of a
  // plain SVG, show the user's actual profile picture.
  const renderAvatar = (uid: string, iconSize = 40, avatarSize?: number) => {
    const size = avatarSize || iconSize
    const url = avatarUrlById(uid)
    if (url) {
      return (
        <img
          src={url}
          alt={getUserName(uid)}
          className="call-avatar-img"
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
          onError={(e) => {
            // If the avatar fails to load, hide the img so the fallback
            // icon shows. We set display:none rather than removing the
            // element so React doesn't complain about DOM mutations.
            e.currentTarget.style.display = 'none'
          }}
        />
      )
    }
    return <UserIcon size={iconSize} />
  }

  // Check if a remote participant has an active video track (camera on).
  // Note: we don't check t.muted because a remote track starts muted
  // until the first RTP packets arrive. Checking muted would hide the
  // video element until data flows, causing a black screen that only
  // appears later "out of nowhere" when the unmute event fires.
  const hasRemoteVideo = (uid: string) => {
    const stream = remoteStreams[uid]
    if (!stream) return false
    const tracks = stream.getVideoTracks()
    return tracks.some((t) => t.enabled && t.readyState !== 'ended')
  }

  const getCurrentUserIdLocal = () => {
    const userStr = localStorage.getItem('atlas_user')
    if (userStr) { try { return JSON.parse(userStr).id } catch { return null } }
    return null
  }

  // Load invite candidates when the panel opens: channel members for
  // channel calls, all workspace users for DM calls.
  useEffect(() => {
    if (!showInvite) return
    if (channelId) {
      authFetch(`${API_URL}/api/group-channel/${channelId}/members`)
        .then((r) => r.json())
        .then((data) => setChannelMembers(data.members || []))
        .catch(() => {})
    } else {
      authFetch(`${API_URL}/api/users`)
        .then((r) => r.json())
        .then((data) => setChannelMembers((data.users || []).map((u: PublicUser) => ({
          id: u.id, channel_id: '', user_id: u.id,
          name: u.name, display_name: u.display_name,
          email: u.email, avatar_url: u.avatar_url, joined_at: '',
        }))))
        .catch(() => {})
    }
  }, [showInvite, channelId, authFetch])

  // Apply output volume to remote audio elements (persistent <audio>)
  useEffect(() => {
    Object.values(remoteAudioOnlyRefs.current).forEach(el => {
      if (el) el.volume = outputVolume
    })
  }, [outputVolume, remoteStreams])

  // Auto-focus on local user when screen sharing starts
  useEffect(() => {
    if (screenSharing) {
      setFocusedTile('local')
    }
  }, [screenSharing])

  // Auto-focus on the first remote participant when they join (if not focused)
  useEffect(() => {
    if (!focusedTile && remoteUserIds.length > 0 && isVideo) {
      setFocusedTile(remoteUserIds[0])
    }
    // Clear focus if the focused participant leaves
    if (focusedTile && focusedTile !== 'local' && !remoteUserIds.includes(focusedTile)) {
      setFocusedTile(remoteUserIds[0] || null)
    }
  }, [remoteUserIds, isVideo])

  // Apply output device (sink) to remote audio elements (persistent <audio>)
  useEffect(() => {
    Object.values(remoteAudioOnlyRefs.current).forEach(el => {
      if (!el || !el.setSinkId) return
      const sink = selectedDevices.audioOut || 'default'
      el.setSinkId(sink).catch(() => {
        el.setSinkId('').catch(() => {})
      })
    })
  }, [selectedDevices.audioOut, remoteStreams])

  // Attach remote streams to persistent <audio> elements for audio playback.
  // These elements are ALWAYS mounted (never unmount on focus/grid changes),
  // so play() is never interrupted by UI layout changes.
  useEffect(() => {
    Object.entries(remoteStreams).forEach(([userId, stream]) => {
      const el = remoteAudioOnlyRefs.current[userId]
      if (!el) return
      if (el.srcObject !== stream) {
        el.srcObject = stream as MediaStream
      }
      el.volume = outputVolume
      if (el.setSinkId) {
        const sink = selectedDevices.audioOut || 'default'
        el.setSinkId(sink).catch(() => {
          el.setSinkId('').catch(() => {})
        })
      }
      el.play().catch(err => {
        console.log('📞 CallView: audio play() failed for', userId, err)
        setTimeout(() => {
          if (el.isConnected) el.play().catch(() => {})
        }, 500)
      })
    })
  }, [remoteStreams, outputVolume, selectedDevices.audioOut])

  // Attach remote streams to <video> elements for VIDEO display only.
  // The <video> elements are muted — audio is handled by the persistent
  // <audio> elements above. This way, when a <video> unmounts during a
  // focus/grid switch, audio is not interrupted.
  useEffect(() => {
    Object.entries(remoteStreams).forEach(([userId, stream]) => {
      const el = remoteAudioRefs.current[userId]
      if (!el || !el.isConnected) return
      if (el.srcObject !== stream) {
        el.srcObject = stream as MediaStream
        el.play().catch(() => {})
      } else if (el.paused) {
        el.play().catch(() => {})
      }
    })
  }, [remoteStreams, focusedTile])

  // Dedicated effect for the focus-mode main video element. This runs
  // whenever the focused user's stream changes OR when remoteHasVideo
  // changes (a track being enabled/disabled doesn't always create a
  // new stream object, but it does change hasRemoteVideo). Without
  // this, the focus video can go black after onunmute replaces the
  // stream because the shared-ref effect above might miss the update.
  const focusedStream = focusedTile && focusedTile !== 'local' ? remoteStreams[focusedTile] : null
  const focusedHasVideo = focusedTile && focusedTile !== 'local' ? hasRemoteVideo(focusedTile) : false
  useEffect(() => {
    if (!focusedStream || focusedTile === 'local') return
    const el = focusVideoRef.current
    if (!el || !el.isConnected) return
    if (el.srcObject !== focusedStream) {
      el.srcObject = focusedStream
    }
    // Always call play() — the element might be paused after a
    // stream swap or tab visibility change.
    el.play().catch(() => {})
  }, [focusedStream, focusedTile, focusedHasVideo])

  // Resume playback when tab becomes visible again — browsers suspend
  // <audio> elements when the tab is hidden or minimized, which
  // makes the call appear to drop (no audio). Calling play() restores it.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      Object.entries(remoteAudioOnlyRefs.current).forEach(([userId, el]) => {
        if (!el || !el.isConnected) return
        if (el.paused) {
          el.play().catch(() => {})
        }
        // Re-attach stream if it was lost
        const stream = remoteStreams[userId]
        if (stream && el.srcObject !== stream) {
          el.srcObject = stream
          el.play().catch(() => {})
        }
      })
      // Also resume the focus video element
      const fv = focusVideoRef.current
      if (fv && fv.isConnected) {
        if (fv.paused) fv.play().catch(() => {})
        const fstream = focusedTile && focusedTile !== 'local' ? remoteStreams[focusedTile] : null
        if (fstream && fv.srcObject !== fstream) {
          fv.srcObject = fstream
          fv.play().catch(() => {})
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleVisibilityChange)
    }
  }, [remoteStreams])

  const handleHangup = () => {
    endCall()
    onClose?.()
  }

  // Track fullscreen state so the button icon/tooltip stays in sync
  // (user can also exit with Esc, which bypasses our toggle).
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    } else {
      focusMainRef.current?.requestFullscreen?.().catch(() => {})
    }
  }

  if (callState === 'idle' && !call.incomingCall) return null

  // Error / failed state — show error with close button
  if (callState === 'failed') {
    return (
      <div className={`call-overlay ${embedded ? 'embedded' : ''}`}>
        <div className="call-container">
          <div className="call-video-area audio-only">
            <div className="call-connecting">
              <div className="call-audio-avatar" style={{ background: 'rgba(248, 81, 73, 0.2)' }}>
                <UserIcon size={40} />
              </div>
              {error && (
                <p className="call-error-failed" style={{ position: 'static', marginTop: 12, maxWidth: 400, textAlign: 'center', lineHeight: 1.5 }}>
                  {error}
                </p>
              )}
            </div>
          </div>
          <div className="call-controls">
            <button
              className="call-control-btn hangup"
              onClick={handleHangup}
              title="Fechar"
            >
              <XIcon size={20} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Incoming call screen
  if (call.incomingCall) {
    const avatar = getUserAvatar(call.incomingCall.fromUserId!)
    const avatarUrl = avatar ? `${API_URL}/uploads/${avatar}` : null
    return (
      <div className={`call-overlay ${embedded ? 'embedded' : ''}`}>
        <div className="call-incoming">
          <div className="call-incoming-avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <UserIcon size={48} />
            )}
          </div>
          <h2>Chamada {call.incomingCall.isVideo ? 'de vídeo' : 'de voz'} recebida</h2>
          <p>{getUserName(call.incomingCall.fromUserId!)} está ligando...</p>
          <div className="call-incoming-actions">
            <button className="call-btn accept" onClick={call.acceptCall}>
              <PhoneIcon size={24} />
              <span>Atender</span>
            </button>
            <button className="call-btn reject" onClick={call.rejectCall}>
              <XIcon size={24} />
              <span>Recusar</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Video area JSX — rendered in the main page OR in the PiP portal.
  const videoAreaContent = (
    <div
      className={`call-video-area ${isVideo ? '' : 'audio-only'} ${focusedTile ? 'focus-mode' : ''}`}
      data-count={totalParticipants}
    >
          {/* ===== Focus mode: main tile + thumbnail sidebar ===== */}
          {focusedTile && remoteUserIds.length > 0 && (
            <>
              {/* Main (focused) tile */}
              <div className="call-focus-main" ref={focusMainRef}>
                {focusedTile === 'local' ? (
                  <>
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`call-video local-video ${screenSharing ? 'screen-share' : ''} ${videoOff && !screenSharing ? 'off' : ''}`}
                      onClick={() => setFocusedTile(null)}
                      style={{ cursor: 'pointer' }}
                    />
                    {videoOff && !screenSharing && (
                      <div className="call-video-off-overlay" onClick={() => setFocusedTile(null)}>
                        {renderAvatar(getCurrentUserIdLocal(), 48, 96)}
                      </div>
                    )}
                    <div className="call-tile-label">Você{screenSharing ? ' (compartilhando)' : ''}</div>
                    {/* Floating controls for local user */}
                    <div className="call-focus-controls" onClick={e => e.stopPropagation()}>
                      <button
                        className={`call-focus-ctrl-btn ${muted ? 'active' : ''}`}
                        onClick={toggleMute}
                        title={muted ? 'Ativar microfone' : 'Silenciar'}
                      >
                        {muted ? <MicOffIcon size={16} /> : <MicIcon size={16} />}
                      </button>
                      <button
                        className={`call-focus-ctrl-btn ${videoOff ? 'active' : ''}`}
                        onClick={toggleVideo}
                        title={videoOff ? 'Ativar câmera' : 'Desligar câmera'}
                      >
                        {videoOff ? <VideoOffIcon size={16} /> : <VideoIcon size={16} />}
                      </button>
                      <button
                        className={`call-focus-ctrl-btn ${screenSharing ? 'active' : ''}`}
                        onClick={toggleScreenShare}
                        title={screenSharing ? 'Parar compartilhamento' : 'Compartilhar tela'}
                      >
                        <ScreenShareIcon size={16} />
                      </button>
                      <button
                        className="call-focus-ctrl-btn"
                        onClick={toggleFullscreen}
                        title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                      >
                        {isFullscreen ? <MinimizeIcon size={16} /> : <MaximizeIcon size={16} />}
                      </button>
                      <button
                        className="call-focus-ctrl-btn back"
                        onClick={() => setFocusedTile(null)}
                        title="Voltar à grade"
                      >
                        <XIcon size={16} />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {(() => {
                      const remoteHasVideo = hasRemoteVideo(focusedTile)
                      return (
                        <>
                          <video
                            ref={focusVideoRef}
                            autoPlay
                            playsInline
                            muted
                            className="call-video remote-video"
                            style={!remoteHasVideo ? { opacity: 0, position: 'absolute', width: '1px', height: '1px', pointerEvents: 'none' } : {}}
                            onClick={() => setFocusedTile(null)}
                          />
                          {!remoteHasVideo && (
                            <div className="call-audio-avatar" style={{ width: 120, height: 120, cursor: 'pointer' }} onClick={() => setFocusedTile(null)}>
                              {renderAvatar(focusedTile, 56, 120)}
                            </div>
                          )}
                        </>
                      )
                    })()}
                    <div className="call-tile-label">{getUserName(focusedTile)}</div>
                    {/* Status indicators for remote user */}
                    <div className="call-focus-controls remote" onClick={e => e.stopPropagation()}>
                      <div className={`call-focus-status ${remoteMuteStates[focusedTile] ? 'muted' : remoteSpeaking[focusedTile] ? 'speaking' : ''}`} title={remoteMuteStates[focusedTile] ? 'Microfone desligado' : remoteSpeaking[focusedTile] ? 'Falando' : 'Microfone ativo'}>
                        {remoteMuteStates[focusedTile] ? <MicOffIcon size={14} /> : <MicIcon size={14} />}
                      </div>
                      {!remoteMuteStates[focusedTile] && (
                        <button
                          className="call-focus-ctrl-btn"
                          onClick={() => muteRemote(focusedTile)}
                          title={`Silenciar ${getUserName(focusedTile)}`}
                        >
                          <MicOffIcon size={16} />
                        </button>
                      )}
                      {remoteMuteStates[focusedTile] && (
                        <button
                          className="call-focus-ctrl-btn"
                          onClick={() => unmuteRemote(focusedTile)}
                          title={`Ativar microfone de ${getUserName(focusedTile)}`}
                        >
                          <MicIcon size={16} />
                        </button>
                      )}
                      <button
                        className="call-focus-ctrl-btn"
                        onClick={toggleFullscreen}
                        title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                      >
                        {isFullscreen ? <MinimizeIcon size={16} /> : <MaximizeIcon size={16} />}
                      </button>
                      <button
                        className="call-focus-ctrl-btn back"
                        onClick={() => setFocusedTile(null)}
                        title="Voltar à grade"
                      >
                        <XIcon size={16} />
                      </button>
                    </div>
                  </>
                )}
                <div className="call-focus-hint">Clique no vídeo para voltar à grade</div>
              </div>

              {/* Thumbnail sidebar */}
              <div className="call-focus-sidebar">
                {/* Local thumbnail (if not focused) */}
                {focusedTile !== 'local' && (
                  <div
                    className={`call-tile call-thumbnail ${isVideo ? '' : 'audio'}`}
                    onClick={() => setFocusedTile('local')}
                    title="Focar em você"
                  >
                    {isVideo ? (
                      <video
                        ref={localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`call-video local-video ${screenSharing ? 'screen-share' : ''} ${videoOff && !screenSharing ? 'off' : ''}`}
                      />
                    ) : null}
                    {(!isVideo || (videoOff && !screenSharing)) && (
                      <div className="call-audio-avatar" style={{ width: 36, height: 36 }}>
                        {renderAvatar(getCurrentUserIdLocal(), 18, 36)}
                      </div>
                    )}
                    {/* Status badges on thumbnail */}
                    <div className="call-thumb-badges">
                      {muted && <span className="call-thumb-badge" title="Microfone desligado"><MicOffIcon size={10} /></span>}
                      {screenSharing && <span className="call-thumb-badge" title="Compartilhando tela"><ScreenShareIcon size={10} /></span>}
                    </div>
                    <div className="call-tile-label">Você{screenSharing ? ' 🖥' : ''}</div>
                  </div>
                )}

                {/* Remote thumbnails */}
                {remoteUserIds.filter(uid => uid !== focusedTile).map(uid => {
                  const remoteHasVideo = hasRemoteVideo(uid)
                  const isRemoteMuted = !!remoteMuteStates[uid]
                  const isSpeaking = !!remoteSpeaking[uid]
                  return (
                  <div
                    key={uid}
                    className={`call-tile call-thumbnail ${remoteHasVideo ? '' : 'audio'} ${isSpeaking && !isRemoteMuted ? 'speaking' : ''}`}
                    onClick={() => setFocusedTile(uid)}
                    title={`Focar em ${getUserName(uid)}`}
                  >
                    <video
                      ref={el => { if (el) remoteAudioRefs.current[uid] = el; else delete remoteAudioRefs.current[uid] }}
                      autoPlay
                      playsInline
                      muted
                      className="call-video remote-video"
                      style={!remoteHasVideo ? { opacity: 0, position: 'absolute', width: '1px', height: '1px', pointerEvents: 'none' } : {}}
                    />
                    {!remoteHasVideo && (
                      <div className="call-audio-avatar" style={{ width: 36, height: 36 }}>
                        {renderAvatar(uid, 18, 36)}
                      </div>
                    )}
                    <div className="call-thumb-badges">
                      {isRemoteMuted && <span className="call-thumb-badge muted" title="Microfone desligado"><MicOffIcon size={10} /></span>}
                      {screenSharing && <span className="call-thumb-badge" title="Compartilhando tela"><ScreenShareIcon size={10} /></span>}
                    </div>
                    <div className="call-tile-label">{getUserName(uid)}</div>
                  </div>
                  )
                })}
              </div>
            </>
          )}

          {/* ===== Grid mode (no focus): all tiles equal size ===== */}
          {!focusedTile && (
            <>
              {/* Remote participants */}
              {remoteUserIds.map(uid => {
                const remoteHasVideo = hasRemoteVideo(uid)
                const isRemoteMuted = !!remoteMuteStates[uid]
                const isSpeaking = !!remoteSpeaking[uid]
                return (
                <div
                  key={uid}
                  className={`call-tile remote clickable ${isSpeaking && !isRemoteMuted ? 'speaking' : ''}`}
                  onClick={() => setFocusedTile(uid)}
                  title={`Focar em ${getUserName(uid)}`}
                >
                  {/* Always render <video> for video display (muted — audio
                      is handled by persistent <audio> elements rendered
                      outside the focus/grid layout). */}
                  <video
                    ref={el => { if (el) remoteAudioRefs.current[uid] = el; else delete remoteAudioRefs.current[uid] }}
                    autoPlay
                    playsInline
                    muted
                    className="call-video remote-video"
                    style={!remoteHasVideo ? { opacity: 0, position: 'absolute', width: '1px', height: '1px', pointerEvents: 'none' } : {}}
                  />
                  {!remoteHasVideo && (
                    <div className="call-audio-avatar">
                      {renderAvatar(uid, 40, 80)}
                    </div>
                  )}
                  {/* Voice activity / mute indicator */}
                  <div className="call-tile-indicators">
                    {isRemoteMuted ? (
                      <span className="call-tile-mic-badge muted" title="Microfone desligado">
                        <MicOffIcon size={14} />
                      </span>
                    ) : isSpeaking ? (
                      <span className="call-tile-mic-badge speaking" title="Falando">
                        <MicIcon size={14} />
                      </span>
                    ) : null}
                  </div>
                  {/* Remote mute/unmute button */}
                  {!isRemoteMuted && (
                    <button
                      className="call-tile-remote-mute"
                      onClick={(e) => { e.stopPropagation(); muteRemote(uid) }}
                      title={`Silenciar ${getUserName(uid)}`}
                    >
                      <MicOffIcon size={12} />
                    </button>
                  )}
                  {isRemoteMuted && (
                    <button
                      className="call-tile-remote-unmute"
                      onClick={(e) => { e.stopPropagation(); unmuteRemote(uid) }}
                      title={`Ativar microfone de ${getUserName(uid)}`}
                    >
                      <MicIcon size={12} />
                    </button>
                  )}
                  <div className="call-tile-label">{getUserName(uid)}</div>
                  <div className="call-tile-focus-hint">Clique para focar</div>
                </div>
                )
              })}

              {/* Participants without tracks yet (joined but no
                  audio/video published — e.g. getUserMedia loading
                  or failed). Show a placeholder tile so the user
                  knows someone is in the call. */}
              {allKnownParticipants.filter((uid: string) => !remoteUserIds.includes(uid)).map((uid: string) => (
                <div
                  key={uid}
                  className="call-tile remote no-track"
                  title={`${getUserName(uid)} — sem mídia`}
                >
                  <div className="call-audio-avatar">
                    {renderAvatar(uid, 40, 80)}
                  </div>
                  <div className="call-tile-label">{getUserName(uid)}</div>
                </div>
              ))}

              {/* Local video (picture-in-picture) */}
              {isVideo && (
                <div
                  className={`call-tile local-pip clickable ${!muted ? 'speaking' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setFocusedTile('local') }}
                  title="Focar em você"
                >
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`call-video local-video ${screenSharing ? 'screen-share' : ''} ${videoOff && !screenSharing ? 'off' : ''}`}
                  />
                  {videoOff && !screenSharing && (
                    <div className="call-video-off-overlay">
                      {renderAvatar(getCurrentUserIdLocal(), 24, 48)}
                    </div>
                  )}
                  <div className="call-tile-indicators">
                    {muted ? (
                      <span className="call-tile-mic-badge muted" title="Microfone desligado">
                        <MicOffIcon size={14} />
                      </span>
                    ) : null}
                  </div>
                  <div className="call-tile-label">Você{screenSharing ? ' (compartilhando)' : ''}</div>
                </div>
              )}

              {/* Audio-only local indicator */}
              {!isVideo && (
                <div
                  className={`call-tile local-audio clickable ${!muted ? 'speaking' : ''}`}
                  onClick={() => setFocusedTile('local')}
                  title="Focar em você"
                >
                  <div className="call-audio-avatar">
                    {renderAvatar(getCurrentUserIdLocal(), 40, 80)}
                  </div>
                  <div className="call-tile-label">Você {muted && '(mudo)'}</div>
                </div>
              )}
            </>
          )}

          {allKnownParticipants.length === 0 && callState !== 'idle' && (
            <div className="call-connecting">
              <div className="call-connecting-spinner" />
              <p>{isChannelCall ? 'Na sala — aguardando participantes...' : (callState === 'calling' ? 'Chamando...' : 'Conectando...')}</p>
            </div>
          )}

          {/* Reconnecting indicator — WS dropped but P2P is still alive */}
          {reconnecting && callState === 'connected' && (
            <div className="call-reconnecting-banner">
              <div className="call-reconnecting-dot" />
              Reconectando sinalização...
            </div>
          )}

          {/* Mic error warning — getUserMedia failed so the user's
              audio isn't being published. They can hear others but
              others can't hear them. Show a prominent warning. */}
          {micError && callState !== 'idle' && callState !== 'failed' && (
            <div className="call-mic-error-banner">
              <MicOffIcon size={16} />
              <span>{micError}</span>
            </div>
          )}
        </div>
  )

  // Controls bar JSX — rendered in the main page OR in the PiP portal.
  // Icon size is 50% smaller in PiP mode to fit the small window.
  const iconSize = showInPiP ? 12 : 20
  const controlsContent = (
    <div className={`call-controls ${showInPiP ? 'pip-mode' : ''}`}>
          <button
            className={`call-control-btn ${muted ? 'active' : ''}`}
            onClick={toggleMute}
            title={muted ? 'Ativar microfone' : 'Silenciar'}
          >
            {muted ? <MicOffIcon size={iconSize} /> : <MicIcon size={iconSize} />}
          </button>

          <button
            className={`call-control-btn ${videoOff ? 'active' : ''}`}
            onClick={toggleVideo}
            title={videoOff ? 'Ativar câmera' : 'Desligar câmera'}
          >
            {videoOff ? <VideoOffIcon size={iconSize} /> : <VideoIcon size={iconSize} />}
          </button>

          <button
            className={`call-control-btn ${screenSharing ? 'active' : ''}`}
            onClick={toggleScreenShare}
            title={screenSharing ? 'Parar compartilhamento' : 'Compartilhar tela'}
          >
            <ScreenShareIcon size={iconSize} />
          </button>

          <button
            className={`call-control-btn ${showSounds ? 'active' : ''}`}
            onClick={() => setShowSounds(!showSounds)}
            title="Tocar som pra todos"
          >
            <PlayIcon size={iconSize} />
          </button>

          <button
            className="call-control-btn"
            onClick={() => setShowInvite(!showInvite)}
            title="Convidar pessoa"
          >
            <PlusIcon size={iconSize} />
          </button>

          <button
            className={`call-control-btn ${showSettings ? 'active' : ''}`}
            onClick={() => {
              const next = !showSettings
              setShowSettings(next)
              if (next && loadDevices) loadDevices()
            }}
            title="Configurações"
          >
            <SettingsIcon size={iconSize} />
          </button>

          {pipSupported && (
            <button
              className={`call-control-btn ${pipActive ? 'active' : ''}`}
              onClick={() => (pipActive ? closePiP() : openPiP())}
              title={pipActive ? 'Desativar Picture-in-Picture' : 'Ativar Picture-in-Picture'}
            >
              <PictureInPictureIcon size={iconSize} />
            </button>
          )}

          <button
            className="call-control-btn hangup-all"
            onClick={() => {
              if (confirm('Finalizar a chamada para todos os participantes?')) {
                endCallForEveryone()
                onClose?.()
              }
            }}
            title="Finalizar chamada para todos"
          >
            <UsersIcon size={iconSize} />
          </button>

          <button
            className="call-control-btn hangup"
            onClick={handleHangup}
            title="Sair da chamada"
          >
            <PhoneIcon size={iconSize} />
          </button>
        </div>
  )

  return (
    <div className={`call-overlay ${embedded ? 'embedded' : ''} ${(hidden || showInPiP) ? 'call-hidden' : ''}`}>
      <div className="call-container">
        {/* Persistent <audio> elements for remote audio playback.
            These are ALWAYS mounted (outside the PiP portal and outside
            the focus/grid layout), so audio is never interrupted when
            the <video> elements unmount during layout changes. */}
        {remoteUserIds.map(uid => (
          <audio
            key={`audio-${uid}`}
            ref={el => { if (el) remoteAudioOnlyRefs.current[uid] = el; else delete remoteAudioOnlyRefs.current[uid] }}
            autoPlay
            style={{ display: 'none' }}
          />
        ))}
        {/* Video area + Controls: rendered in PiP portal when the tab
            is hidden or user navigated away; rendered in the page when
            the tab is visible and on the call page. PiP window stays
            open the whole time — we just move content in/out.
            When content is in the page, we render a minimal placeholder
            in the PiP window so it doesn't show empty/stale content. */}
        {showInPiP ? (
          <>
            {createPortal(videoAreaContent, pipContainer)}
            {createPortal(controlsContent, pipContainer)}
          </>
        ) : (
          <>
            {videoAreaContent}
            {error && (
              <div className="call-error">{error}</div>
            )}
            {controlsContent}
            {/* Minimal placeholder in PiP window when content is in page */}
            {pipActive && pipContainer && createPortal(
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#888', fontSize: 13, flexDirection: 'column', gap: 8 }}>
                <span>Chamada ativa</span>
              </div>,
              pipContainer
            )}
          </>
        )}
        {showSettings && (
          <div className="call-settings">
            <div className="call-settings-header">
              <h3>Configurações de Áudio e Vídeo</h3>
              <button className="icon-btn" onClick={() => setShowSettings(false)}>
                <XIcon size={16} />
              </button>
            </div>

            {/* Device selection */}
            <div className="call-settings-section">
              <label>Microfone</label>
              <select
                value={selectedDevices.audioIn || 'default'}
                onChange={e => changeDevice('audioIn', e.target.value)}
              >
                {!devices.audioIn.some((d) => d.deviceId === 'default') && (
                  <option value="default">Padrão do sistema</option>
                )}
                {devices.audioIn.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.deviceId === 'default' ? `Padrão do sistema (${d.label || 'auto'})` : (d.label || `Microfone ${d.deviceId.slice(0, 8)}`)}
                  </option>
                ))}
              </select>
            </div>

            <div className="call-settings-section">
              <label>Alto-falante</label>
              <select
                value={selectedDevices.audioOut || 'default'}
                onChange={e => changeDevice('audioOut', e.target.value)}
              >
                {!devices.audioOut.some((d) => d.deviceId === 'default') && (
                  <option value="default">Padrão do sistema</option>
                )}
                {devices.audioOut.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.deviceId === 'default' ? `Padrão do sistema (${d.label || 'auto'})` : (d.label || `Alto-falante ${d.deviceId.slice(0, 8)}`)}
                  </option>
                ))}
              </select>
            </div>

            {isVideo && (
              <div className="call-settings-section">
                <label>Câmera</label>
                <select
                  value={selectedDevices.videoIn}
                  onChange={e => changeDevice('videoIn', e.target.value)}
                >
                  {devices.videoIn.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Câmera ${d.deviceId.slice(0, 8)}`}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Volume controls */}
            <div className="call-settings-section">
              <label>
                <VolumeIcon size={14} /> Volume do Microfone
                <span className="volume-value">{Math.round(inputVolume * 100)}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={inputVolume}
                onChange={e => changeInputVolume(parseFloat(e.target.value))}
                className="volume-slider"
              />
            </div>

            <div className="call-settings-section">
              <label>
                <VolumeIcon size={14} /> Volume do Alto-falante
                <span className="volume-value">{Math.round(outputVolume * 100)}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={outputVolume}
                onChange={e => changeOutputVolume(parseFloat(e.target.value))}
                className="volume-slider"
              />
            </div>

            <div className="call-settings-section">
              <p className="call-settings-hint">
                Supressão de ruído, eco e ganho do navegador sempre ativos no microfone
              </p>
            </div>
          </div>
        )}

        {/* Sound board menu */}
        {showSounds && (
          <div className="call-settings call-sounds-menu">
            <div className="call-settings-header">
              <h3>Tocar som pra todos</h3>
              <button className="icon-btn" onClick={() => setShowSounds(false)}>
                <XIcon size={16} />
              </button>
            </div>
            <div className="call-sounds-list">
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/porque-nao-trabalha.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Porque não trabalha... vai toma sua cu</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/1.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Dilma e a Meta!!</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/2.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Pense no Lula</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/3.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Lula - Feijão puro</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/4.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Lula: o pobre não precisa de muita coisa</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/5.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Lula: vai comer abóbora, melancia, banana, manga</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/6.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Dilma: quem ganhar ou perder vai todo mundo perder</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/7.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Caminhoneiro / diesel / pix - meme</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/8.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Cala sua boca e não fala comigo</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/9.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Tropa de Elite: cadê a bandoleira??</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/10.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Tropa de Elite: copa de elite (paródia)</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/11.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Cadê o baiano?</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/12.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Tropa de Elite: vai dar merda isso</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/13.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Tropa de Elite: Aspira / Milhem Cortaz + André Ramiro</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/14.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>O conceito de estratégia</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/16.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Eu vou te ajudar</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/17.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Efeitos Sonoros - Chicote</span>
              </button>
              <button className="call-sound-item" onClick={() => { playSoundToAll('/sounds/shorts/18.mp3'); setShowSounds(false) }}>
                <PlayIcon size={14} />
                <span>Comia calango quando era criança</span>
              </button>
            </div>
          </div>
        )}

        {/* Invite panel */}
        {showInvite && (
          <div className="call-settings call-invite-panel">
            <div className="call-settings-header">
              <h3>Convidar para a chamada</h3>
              <button className="icon-btn" onClick={() => setShowInvite(false)}>
                <XIcon size={16} />
              </button>
            </div>
            <div className="call-invite-list">
              {channelMembers.length === 0 && (
                <p className="call-settings-hint">Carregando membros...</p>
              )}
              {channelMembers.filter(m => m.email !== 'atlas@colossus7.com').map(m => {
                const inCall = allKnownParticipants.includes(m.user_id) || Object.keys(remoteStreams).includes(m.user_id) || m.user_id === getCurrentUserIdLocal()
                const isMe = m.user_id === getCurrentUserIdLocal()
                const name = m.display_name || m.name || m.email || 'Usuário'
                const initial = (name || '?').trim().charAt(0).toUpperCase()
                const inviteState = invitingUsers[m.user_id]
                return (
                  <div key={m.user_id} className="call-invite-item">
                    <div className="call-invite-user">
                      {avatarUrlById(m.user_id) ? (
                        <img src={avatarUrlById(m.user_id) || undefined} className="call-invite-avatar" alt="" />
                      ) : (
                        <div className="call-invite-avatar call-invite-avatar-fallback">{initial}</div>
                      )}
                      <span>{name}{isMe ? ' (você)' : ''}</span>
                    </div>
                    <div className="call-invite-actions">
                      {inCall ? (
                        !isMe && (
                          <button
                            className="call-invite-btn call-invite-remove"
                            onClick={() => removeFromCall(m.user_id)}
                            title="Remover da chamada"
                          >
                            Remover
                          </button>
                        )
                      ) : inviteState === 'calling' ? (
                        <span className="call-invite-status calling">
                          <span className="call-invite-spinner" />
                          Chamando...
                        </span>
                      ) : inviteState === 'rejected' ? (
                        <>
                          <span className="call-invite-status rejected">Recusou</span>
                          <button
                            className="call-invite-btn call-invite-silent"
                            onClick={() => inviteToCallSilent(m.user_id)}
                            title="Enviar mensagem no chat"
                          >
                            Convidar
                          </button>
                          <button
                            className="call-invite-btn"
                            onClick={() => inviteToCall(m.user_id)}
                            title="Tocar ringtone"
                          >
                            Chamar
                          </button>
                          <button
                            className="call-invite-btn call-invite-force"
                            onClick={() => forceJoinCall(m.user_id)}
                            title="Forçar entrada (sem ringtone)"
                          >
                            Forçar
                          </button>
                        </>
                      ) : inviteState === 'timeout' ? (
                        <>
                          <span className="call-invite-status timeout">Sem resposta</span>
                          <button
                            className="call-invite-btn call-invite-silent"
                            onClick={() => inviteToCallSilent(m.user_id)}
                            title="Enviar mensagem no chat"
                          >
                            Convidar
                          </button>
                          <button
                            className="call-invite-btn"
                            onClick={() => inviteToCall(m.user_id)}
                            title="Tocar ringtone"
                          >
                            Chamar
                          </button>
                          <button
                            className="call-invite-btn call-invite-force"
                            onClick={() => forceJoinCall(m.user_id)}
                            title="Forçar entrada (sem ringtone)"
                          >
                            Forçar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="call-invite-btn call-invite-silent"
                            onClick={() => inviteToCallSilent(m.user_id)}
                            title="Enviar mensagem no chat"
                          >
                            Convidar
                          </button>
                          <button
                            className="call-invite-btn"
                            onClick={() => inviteToCall(m.user_id)}
                            title="Tocar ringtone"
                          >
                            Chamar
                          </button>
                          <button
                            className="call-invite-btn call-invite-force"
                            onClick={() => forceJoinCall(m.user_id)}
                            title="Forçar entrada (sem ringtone)"
                          >
                            Forçar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
